import React, { memo, useCallback, useContext, useState } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { SubmitConfirmModal } from 'src/components/shared/SubmitConfirmModal'
import { SendMajor } from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import { ContractorDetailsModalContext } from '../ContractorDetailsModal.context'
import { useResendInviteCooldown } from 'src/hooks/useResendInviteCooldown'
import type { SendReminderResponse } from 'src/interfaces/reminder'
import { ResendConfirmText } from './ResendConfirmText'
import { ResendCooldownHint } from './ResendCooldownHint'

interface Props {
  contractorName: string
  resendAvailableAt: string | null
  onResend(): Promise<SendReminderResponse | null>
  buttonLabel: string
  confirmTitle: string
  confirmTextPrefix: string
  helperLabel: string
  limitReachedText: string
}

const ResendWithCooldownComponent = ({
  contractorName,
  resendAvailableAt,
  onResend,
  buttonLabel,
  confirmTitle,
  confirmTextPrefix,
  helperLabel,
  limitReachedText,
}: Props) => {
  const { statusIsUpdating } = useContext(ContractorDetailsModalContext)

  const {
    isDisabled: isCooldownActive,
    timeRemaining,
    isLimitReached,
    setResendAvailableAt,
  } = useResendInviteCooldown(resendAvailableAt)

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false)
  }, [])

  const handleSubmit = useCallback(async () => {
    const response = await onResend()

    if (response) {
      setResendAvailableAt(response.resendAvailableAt)
    }

    setIsConfirmOpen(false)
  }, [onResend, setResendAvailableAt])

  const isDisabled = statusIsUpdating || isCooldownActive

  return (
    <div css={tw`mt-2 w-full`}>
      <Button
        endIcon={
          <SendMajor
            fill={colors.PRIMARY_PURPLE_500}
            width={20}
            height={20}
            css={isDisabled && tw`opacity-50`}
          />
        }
        css={tw`h-[36px] flex gap-0`}
        color="light"
        size="small"
        fullWidth
        onClick={handleOpenConfirm}
        disabled={isDisabled}
        data-testid="contractor-actions-resend-invite-btn"
      >
        {buttonLabel}
      </Button>
      {isCooldownActive && timeRemaining !== null && (
        <ResendCooldownHint
          helperLabel={helperLabel}
          limitReachedText={limitReachedText}
          timeRemaining={timeRemaining}
          isLimitReached={isLimitReached}
        />
      )}
      <SubmitConfirmModal
        isOpen={isConfirmOpen}
        title={confirmTitle}
        text={<ResendConfirmText prefix={confirmTextPrefix} contractorName={contractorName} />}
        submitLabel={buttonLabel}
        onClose={handleCloseConfirm}
        onSubmit={handleSubmit}
      />
      <div css={tw`mb-2`} />
    </div>
  )
}

export const ResendWithCooldown = memo(ResendWithCooldownComponent)
